import { clamp } from "@captions-web/utils/clamp";

import { audioBufferToWav } from "./audio-buffer-to-wav";
import { AUDIO_PROCESSOR_VISUALIZATION_DOWNSAMPLE_FACTOR } from "./constants";

export type AudioProcessorState = "initial" | "loading" | "ready" | "error";
export type AudioProcessorEvent = "timeupdate" | "play" | "pause" | "loaded" | "ended" | "error";
export type AudioProcessorEventPayload = {
  currentTimeMs: number;
  state: AudioProcessorState;
  duration: number;
  isPlaying: boolean;
  error?: unknown;
};
export type AudioProcessorEventCallback = (data?: AudioProcessorEventPayload) => void;

export class AudioProcessor {
  #audioContext: AudioContext;
  #audioBuffer: AudioBuffer | null = null;
  #file: File | null = null;
  #sourceNode: AudioBufferSourceNode | null = null;
  #startTimeMs: number = 0;
  #pausedAt: number = 0;
  #isPlaying: boolean = false;
  #animationFrameId: number | null = null;
  #eventListeners: Map<AudioProcessorEvent, Set<AudioProcessorEventCallback>> = new Map();
  #error: unknown;
  data: Float32Array | null = null;
  downsampledData: Float32Array | null = null;
  state: AudioProcessorState = "initial";
  loopRegion: { startMs: number; endMs: number } | undefined;

  constructor() {
    this.#audioContext = new AudioContext();
  }

  // returns the audio duration in ms
  get durationMs() {
    return (this.#audioBuffer ? this.#audioBuffer.duration : 0) * 1000;
  }

  get isPlaying() {
    return this.#isPlaying;
  }

  get metadata() {
    const metadata = {
      durationMs: this.durationMs,
      sampleRate: this.#audioBuffer?.sampleRate,
      numberOfChannels: this.#audioBuffer?.numberOfChannels,
      length: this.#audioBuffer?.length,
    };
    return metadata;
  }

  get currentTimeMs() {
    if (!this.#isPlaying) {
      return this.#pausedAt;
    }
    const elapsed = performance.now() - this.#startTimeMs;
    return this.#pausedAt + elapsed;
  }

  on(eventType: AudioProcessorEvent, callback: AudioProcessorEventCallback) {
    if (!this.#eventListeners.has(eventType)) {
      this.#eventListeners.set(eventType, new Set());
    }

    const listeners = this.#eventListeners.get(eventType);
    listeners?.add(callback);
  }

  off(eventType: AudioProcessorEvent) {
    const listeners = this.#eventListeners.get(eventType);
    if (listeners) {
      listeners.clear();
      if (listeners.size === 0) {
        this.#eventListeners.delete(eventType);
      }
    }
  }

  async loadAudio(file: File): Promise<void> {
    if (this.state === "ready") {
      return;
    }
    try {
      this.#file = file;
      this.state = "loading";
      const arrayBuffer = await file.arrayBuffer();
      this.#audioBuffer = await this.#audioContext.decodeAudioData(arrayBuffer);
      // default to left channel, can add stereo visualization if needed in the future
      const channelData = this.#audioBuffer.getChannelData(0);
      this.data = channelData;
      const samplesPerPixel = Math.max(
        1,
        Math.floor(channelData.length / AUDIO_PROCESSOR_VISUALIZATION_DOWNSAMPLE_FACTOR)
      );
      this.downsampledData = this.#downsample(channelData, samplesPerPixel);
      this.state = "ready";
      this.#emit("loaded");
    } catch (error) {
      console.error(error);
      this.state = "error";
      this.#error = error;
      this.#emit("error");
    }
  }

  play() {
    if (!this.#audioBuffer) {
      console.warn("called play on audio context before audioBuffer is defined");
      return;
    }
    if (this.#isPlaying) {
      // temporarily stop
      return;
    }

    if (this.#audioContext.state === "suspended") {
      this.#audioContext.resume();
    }

    this.#sourceNode = this.#audioContext.createBufferSource();
    this.#sourceNode.buffer = this.#audioBuffer;
    this.#sourceNode.connect(this.#audioContext.destination);
    const startAt = this.#pausedAt;
    // start playback with the offset in seconds
    const offsetSeconds = startAt / 1000;
    this.#sourceNode.start(0, offsetSeconds);
    this.#startTimeMs = performance.now();
    this.#isPlaying = true;
    this.#startTimeUpdateInterval();
    this.#emit("play");
  }

  pause() {
    if (!this.#isPlaying || !this.#sourceNode) {
      return;
    }
    const elapsed = performance.now() - this.#startTimeMs;
    this.#pausedAt += elapsed;
    this.#sourceNode.stop();
    this.#sourceNode = null;
    this.#isPlaying = false;

    this.#stopTimeUpdateInterval();

    this.#emit("pause");
  }

  stop() {
    if (!this.#sourceNode) {
      return;
    }

    this.#sourceNode.stop();
    this.#sourceNode.disconnect();
    this.#sourceNode = null;
    this.#pausedAt = this.loopRegion ? this.loopRegion.startMs : 0;
    this.#isPlaying = false;

    this.#stopTimeUpdateInterval();
    this.#emit("ended");
  }

  seek(timeMs: number): void {
    const clamped = clamp(timeMs, 0, this.durationMs);
    if (this.#isPlaying) {
      this.pause();
      this.#pausedAt = clamped;
      this.play();
    } else {
      this.#pausedAt = clamped;
      this.#emit("timeupdate");
    }
  }

  setLoopRegion(startMs: number, endMs: number): void {
    if (startMs < 0 || endMs <= startMs || endMs > this.durationMs) {
      console.warn("Invalid loop region parameters");
      return;
    }

    this.loopRegion = { startMs, endMs };
    const currentTimeMs = this.currentTimeMs;
    let pausedAt = currentTimeMs;
    if (currentTimeMs > endMs) {
      pausedAt = endMs;
    } else if (currentTimeMs < startMs) {
      pausedAt = startMs;
    }
    this.#pausedAt = pausedAt;
    this.seek(pausedAt);
  }

  destroy() {
    if (this.#isPlaying) {
      this.stop();
    }

    if (this.#sourceNode) {
      this.#sourceNode.disconnect();
      this.#sourceNode = null;
    }

    this.#stopTimeUpdateInterval();

    this.#eventListeners.clear();

    if (this.#audioContext && this.#audioContext.state !== "closed") {
      this.#audioContext.close();
    }

    this.data = null;
    this.downsampledData = null;
    this.#audioBuffer = null;
    this.#file = null;

    this.state = "initial";
  }

  trimAudioFile({ startMs, endMs }: { startMs: number; endMs: number }): File | undefined {
    if (!this.#audioBuffer) {
      console.warn("No audio buffer to trim.");
      return;
    }

    const startSec = startMs / 1000;
    const endSec = endMs / 1000;
    const sampleRate = this.#audioBuffer.sampleRate;
    const startSample = Math.floor(startSec * sampleRate);
    const endSample = Math.floor(endSec * sampleRate);
    const trimmedLength = endSample - startSample;

    if (startSample >= endSample || trimmedLength <= 0) {
      console.warn("Invalid trim region.");
      return;
    }

    const numberOfChannels = this.#audioBuffer.numberOfChannels;
    const trimmedAudioBuffer = this.#audioContext.createBuffer(
      numberOfChannels,
      trimmedLength,
      sampleRate
    );

    for (let channel = 0; channel < numberOfChannels; channel++) {
      const channelData = this.#audioBuffer.getChannelData(channel);
      const trimmedChannelData = trimmedAudioBuffer.getChannelData(channel);
      for (let i = 0; i < trimmedLength; i++) {
        trimmedChannelData[i] = channelData[i + startSample];
      }
    }

    const wav = audioBufferToWav(trimmedAudioBuffer);
    const fileName = "trimmed_audio.wav";
    const fileType = "audio/wav";
    const trimmedFile = new File([wav], fileName, { type: fileType });
    return trimmedFile;
  }

  #startTimeUpdateInterval() {
    this.#emit("timeupdate");
    if (!this.#isPlaying || this.#animationFrameId !== null) {
      return;
    }

    this.#animationFrameId = requestAnimationFrame(this.#updateTime);
  }

  #stopTimeUpdateInterval() {
    if (this.#animationFrameId !== null) {
      cancelAnimationFrame(this.#animationFrameId);
      this.#animationFrameId = null;
    }
  }

  #updateTime = () => {
    if (this.#isPlaying && this.loopRegion?.endMs && this.currentTimeMs >= this.loopRegion.endMs) {
      this.pause();
      this.#pausedAt = this.loopRegion.startMs;
      this.play();
    }

    this.#emit("timeupdate");

    this.#animationFrameId = requestAnimationFrame(this.#updateTime);
    if (this.currentTimeMs > this.durationMs && this.#isPlaying) {
      this.stop();
    }
  };

  #downsample(data: Float32Array, samplesPerPixel: number): Float32Array {
    const result = new Float32Array(Math.ceil(data.length / samplesPerPixel));

    for (let i = 0; i < result.length; i++) {
      const startIndex = i * samplesPerPixel;
      const endIndex = Math.min(startIndex + samplesPerPixel, data.length);

      let maxValue = 0;
      for (let j = startIndex; j < endIndex; j++) {
        const absValue = Math.abs(data[j]);
        if (absValue > maxValue) {
          maxValue = absValue;
        }
      }

      result[i] = maxValue;
    }

    return result;
  }

  #emit(eventType: AudioProcessorEvent): void {
    const listeners = this.#eventListeners.get(eventType);
    if (listeners) {
      listeners.forEach((callback) =>
        callback({
          currentTimeMs: this.currentTimeMs,
          state: this.state,
          duration: this.durationMs,
          isPlaying: this.isPlaying,
          error: this.#error,
        })
      );
    }
  }
}
